import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import platformStorageQuery from "./queries/platformStorageQuery";
import platformStorageLimitsQuery from "./queries/platformStorageLimitsQuery";

const PAGE_SIZE = 1000;
const STORAGE_LIMIT_NAMESPACE = "max_cloud_storage_bytes";

const normalizeBreakdown = (breakdown) => {
  if (!breakdown) return {};

  if (typeof breakdown === "string") {
    try {
      return JSON.parse(breakdown);
    } catch (error) {
      console.warn("Unable to parse org_storage.breakdown", error);
      return {};
    }
  }

  if (typeof breakdown === "object") {
    return breakdown;
  }

  return {};
};

const normalizeStorageRecord = (node) => ({
  ...node,
  orgId: node?.org_pointer?.objectId || null,
  orgName: node?.org_pointer?.nome || null,
  orgStatus: node?.org_pointer?.stato || null,
  orgInstance: node?.org_pointer?.tipo_istanza || null,
  orgExpiresAt: node?.org_pointer?.data_scadenza || null,
  orgGiottoExpiresAt: node?.org_pointer?.data_scadenza_giotto || null,
  total_bytes: Number(node?.total_bytes || 0),
  total_files: Number(node?.total_files || 0),
  breakdown: normalizeBreakdown(node?.breakdown),
});

const normalizeStorageLimitRecord = (node) => ({
  objectId: node?.objectId || null,
  orgId: node?.org_pointer?.objectId || null,
  amount: Number(node?.amount || 0),
  updatedAt: node?.updatedAt || null,
});

const getLatestUpdatedAt = (records) => {
  const latest = records.reduce((currentLatest, record) => {
    const date = new Date(record.updatedAt);
    if (Number.isNaN(date.getTime())) return currentLatest;
    return !currentLatest || date > currentLatest ? date : currentLatest;
  }, null);

  return latest ? latest.toISOString() : null;
};

const getCurrentRecordsByOrg = (records) => {
  const recordsByOrg = new Map();

  records.forEach((record) => {
    const key = record.orgId || record.objectId;
    const existingRecord = recordsByOrg.get(key);
    const recordUpdatedAt = new Date(record.updatedAt).getTime();
    const existingUpdatedAt = existingRecord
      ? new Date(existingRecord.updatedAt).getTime()
      : 0;

    if (!existingRecord || recordUpdatedAt > existingUpdatedAt) {
      recordsByOrg.set(key, record);
    }
  });

  return Array.from(recordsByOrg.values());
};

const getCurrentLimitsByOrg = (records) => {
  const limitsByOrg = new Map();

  records.forEach((record) => {
    if (!record.orgId) return;

    const existingRecord = limitsByOrg.get(record.orgId);
    const recordUpdatedAt = new Date(record.updatedAt).getTime();
    const existingUpdatedAt = existingRecord
      ? new Date(existingRecord.updatedAt).getTime()
      : 0;

    if (!existingRecord || recordUpdatedAt > existingUpdatedAt) {
      limitsByOrg.set(record.orgId, record);
    }
  });

  return limitsByOrg;
};

const usePlatformStorage = () => {
  const [storageRecords, setStorageRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [storageLimitCount, setStorageLimitCount] = useState(0);
  const [totalStorageLimitBytes, setTotalStorageLimitBytes] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const fetchPlatformStorage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
      const records = [];
      const limitRecords = [];
      let count = 0;
      let skip = 0;
      let keepFetching = true;

      while (keepFetching) {
        const response = await axios({
          method: "post",
          url: graphqlUrl,
          data: {
            query: platformStorageQuery,
            variables: {
              first: PAGE_SIZE,
              skip,
            },
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const responseData = response.data;
        const graphqlErrors = responseData?.errors || responseData?.data?.errors;

        if (graphqlErrors?.length) {
          throw new Error(graphqlErrors[0].message);
        }

        const orgStorages =
          responseData?.org_storages || responseData?.data?.org_storages || {};
        const edges = orgStorages.edges || [];
        count = Number(orgStorages.count || count || 0);

        records.push(
          ...edges.map((edge) => edge.node).filter(Boolean).map(normalizeStorageRecord)
        );

        skip += edges.length;
        keepFetching =
          edges.length > 0 && (count > 0 ? records.length < count : edges.length === PAGE_SIZE);
      }

      let limitsCount = 0;
      let limitsSkip = 0;
      let keepFetchingLimits = true;

      while (keepFetchingLimits) {
        const response = await axios({
          method: "post",
          url: graphqlUrl,
          data: {
            query: platformStorageLimitsQuery,
            variables: {
              first: PAGE_SIZE,
              skip: limitsSkip,
              namespace: STORAGE_LIMIT_NAMESPACE,
            },
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const responseData = response.data;
        const graphqlErrors = responseData?.errors || responseData?.data?.errors;

        if (graphqlErrors?.length) {
          throw new Error(graphqlErrors[0].message);
        }

        const orgLimits =
          responseData?.org_limits || responseData?.data?.org_limits || {};
        const edges = orgLimits.edges || [];
        limitsCount = Number(orgLimits.count || limitsCount || 0);

        limitRecords.push(
          ...edges.map((edge) => edge.node).filter(Boolean).map(normalizeStorageLimitRecord)
        );

        limitsSkip += edges.length;
        keepFetchingLimits =
          edges.length > 0 &&
          (limitsCount > 0 ? limitRecords.length < limitsCount : edges.length === PAGE_SIZE);
      }

      const limitsByOrg = getCurrentLimitsByOrg(limitRecords);
      const currentRecords = getCurrentRecordsByOrg(records).map((record) => {
        const storageLimit = limitsByOrg.get(record.orgId);

        return {
          ...record,
          storageLimitBytes: storageLimit?.amount ?? null,
          storageLimitId: storageLimit?.objectId ?? null,
          storageLimitUpdatedAt: storageLimit?.updatedAt ?? null,
        };
      });

      setStorageRecords(currentRecords);
      setTotalCount(currentRecords.length);
      setStorageLimitCount(limitsByOrg.size);
      setTotalStorageLimitBytes(
        Array.from(limitsByOrg.values()).reduce((sum, record) => sum + record.amount, 0)
      );
      setLastUpdatedAt(getLatestUpdatedAt(currentRecords));
    } catch (err) {
      console.error("Errore durante il recupero dello storage piattaforma:", err);
      setError(err.message || "Errore nel caricamento dello storage piattaforma");
      setStorageRecords([]);
      setTotalCount(0);
      setStorageLimitCount(0);
      setTotalStorageLimitBytes(0);
      setLastUpdatedAt(null);
    } finally {
      setLoading(false);
    }
  }, [env, getTokenSilently]);

  useEffect(() => {
    fetchPlatformStorage();
  }, [fetchPlatformStorage]);

  return {
    storageRecords,
    totalCount,
    storageLimitCount,
    totalStorageLimitBytes,
    lastUpdatedAt,
    loading,
    error,
    refetch: fetchPlatformStorage,
  };
};

export default usePlatformStorage;
